// Custom hook for loading task assignments
// Handles loading and displaying assignments (read-only)
// CHANGE: Created for assignment management
// CHANGE: Fixed user fields to use first_name and last_name instead of non-existent full_name column
// CHANGE: Simplified to read-only mode - removed edit and cancel functions
// CHANGE: Added nextOccurrenceDate field for precise occurrence display
// CHANGE: Added request ID pattern to prevent stale async responses from overwriting current state
// CHANGE: Removed all debug console statements for production

import { useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { addYears } from '../lib/dateUtils';
import { getNextOccurrenceForTask } from '../lib/occurrences';
import type { AssignedTask, RecurrenceRule, AssignedTaskOccurrence } from '../lib/supabase';

export interface AssignmentWithDetails {
  id: string;
  template_id: string;
  assigned_to_user_id: string;
  site_id: string;
  assigned_date: string;
  scheduled_time: string | null;
  duration_override: number | null;
  status: string;
  is_recurring: boolean;
  recurrence_rule_id: string | null;
  recurrence_start_date: string | null;
  recurrence_end_date: string | null;
  nextOccurrenceDate: string | null;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    username: string;
  } | null;
  site?: {
    id: string;
    name: string;
  } | null;
  recurrence_rule?: {
    id: string;
    frequency: 'daily' | 'weekly' | 'monthly';
    interval: number;
    days_of_week: number[];
    day_of_month: number | null;
    week_of_month: number | null;
  } | null;
}

export function useAssignments(templateId: string) {
  const [assignments, setAssignments] = useState<AssignmentWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadAssignments = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('assigned_tasks')
        .select(`
          *,
          user:users!assigned_tasks_assigned_to_user_id_fkey (
            id,
            username,
            first_name,
            last_name
          ),
          site:sites (
            id,
            name
          ),
          recurrence_rule:recurrence_rules (
            id,
            frequency,
            interval,
            days_of_week,
            day_of_month,
            week_of_month
          )
        `)
        .eq('template_id', templateId)
        .order('assigned_date', { ascending: true });

      if (fetchError) {
        throw fetchError;
      }

      if (requestId !== requestIdRef.current) return;

      if (!data || data.length === 0) {
        setAssignments([]);
        return;
      }

      // Calculate the search period (today + 1 year)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const oneYearLater = addYears(today, 1);

      // Load all exceptions for recurring assignments in a single query
      const recurringAssignmentIds = data
        .filter(a => a.is_recurring)
        .map(a => a.id);

      let allExceptions: AssignedTaskOccurrence[] = [];
      if (recurringAssignmentIds.length > 0) {
        const { data: exceptionsData, error: exceptionsError } = await supabase
          .from('assigned_task_occurrences')
          .select('*')
          .in('assigned_task_id', recurringAssignmentIds)
          .gte('occurrence_date', today.toISOString().split('T')[0])
          .lte('occurrence_date', oneYearLater.toISOString().split('T')[0]);

        if (exceptionsError) {
        } else {
          allExceptions = exceptionsData || [];
        }
      }

      if (requestId !== requestIdRef.current) return;

      const exceptionsByAssignment = new Map<string, AssignedTaskOccurrence[]>();
      allExceptions.forEach(exc => {
        if (!exceptionsByAssignment.has(exc.assigned_task_id)) {
          exceptionsByAssignment.set(exc.assigned_task_id, []);
        }
        exceptionsByAssignment.get(exc.assigned_task_id)!.push(exc);
      });

      // Calculate nextOccurrenceDate for each assignment and filter
      const enrichedAssignments = data
        .map(assignment => {
          let nextOccurrenceDate: string | null = null;

          if (assignment.is_recurring && assignment.recurrence_rule) {
            // For recurring assignments, calculate next occurrence
            const exceptions = exceptionsByAssignment.get(assignment.id) || [];
            nextOccurrenceDate = getNextOccurrenceForTask(
              assignment as AssignedTask,
              assignment.recurrence_rule as RecurrenceRule,
              today,
              oneYearLater,
              exceptions
            );
          } else {
            // For non-recurring assignments, use assigned_date if it's in the future
            const assignedDate = new Date(assignment.assigned_date + 'T00:00:00');
            nextOccurrenceDate = assignedDate >= today ? assignment.assigned_date : null;
          }

          return {
            ...assignment,
            nextOccurrenceDate
          } as AssignmentWithDetails;
        })
        .filter(assignment => assignment.nextOccurrenceDate !== null)
        .sort((a, b) => {
          // Sort by next occurrence date
          if (!a.nextOccurrenceDate || !b.nextOccurrenceDate) return 0;
          return a.nextOccurrenceDate.localeCompare(b.nextOccurrenceDate);
        });

      setAssignments(enrichedAssignments);
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [templateId]);

  return {
    assignments,
    loading,
    error,
    loadAssignments
  };
}
